import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useServerError } from "../Utils/handleServerError";
import UserService from "../Services/UserService";
import "../CSS/profit-log.css";
import CreateStake from "../Components/CreateStake";
import PopModal from "../Components/ModelLayout/ModalLayout";
import CompleteStakeModal from "../Components/CompleteStakeModal";
import StakeService from "../Services/StakeService";
import { updateUser } from "../Redux/slices/usersSlice";

const UserStakeLog = () => {
  const dispatch = useDispatch();
  const { users, selectedUser } = useSelector((state) => state.usersSlice);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);

  const { token } = useSelector((state) => state.authSlice);

  const [error, setError] = useState(null);

  useServerError(error);

  function openErrorMessage(error) {
    setError(error);

    setTimeout(() => {
      setError(null);
    }, 4000);
  }

  const selectedUserData = useMemo(() => {
    return users.filter((user) => user.user_id === selectedUser)[0] || null;
  }, [selectedUser, users]);

  const fetchUserStakes = async (user_id) => {
    try {
      setLoading(true);
      const [res, withdrawalRes] = await Promise.all([
        UserService.get_user_stake_log(token, user_id),
        UserService.get_user_withdrawal_log(token, user_id),
      ]);

      setData(res.data);
      // Staking withdrawals are stored with the user's other withdrawals, so pick them out
      setWithdrawals(
        withdrawalRes.data
          .filter((item) => item.type === "Staking" || item.type === "StakingBalance")
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } catch (error) {
      openErrorMessage(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedUser && token) {
      fetchUserStakes(selectedUser);
    }
  }, [selectedUser, token]);

  const [createStakeProps, setCreateStakeProps] = useState(null);
  const [completeStakeProps, setCompleteStakeProps] = useState(null);

  const openCompleteStakeModal = (handleOk, handleCancel, initialAmount, stakeAmount) => 
    setCompleteStakeProps({ handleOk, handleCancel, initialAmount, stakeAmount });

  const openCreateStakeModal = (handleOk, handleCancel) =>
    setCreateStakeProps({ handleOk, handleCancel });

  const closeCreateStakeModal = () => setCreateStakeProps(null);
  const closeCompleteStakeModal = () => setCompleteStakeProps(null);

  const createStake = () =>
    new Promise((res) => {
      openCreateStakeModal((data) => {
        res(data);
        closeCreateStakeModal();
      }, closeCreateStakeModal);
    });

  const confirmStake = (initialAmount, stakeAmount) =>
    new Promise((res) => {
      openCompleteStakeModal(res, closeCompleteStakeModal, initialAmount, stakeAmount);
    });

  // Keeps the user's balances in step with every stake change
  const syncBalances = (balances) => {
    if (balances) {
      dispatch(updateUser({ user_id: selectedUser, ...balances }));
    }
  };

  const handleAdd = async () => {
    try {
      const created = await createStake();

      if (created) {
        const { balances, ...stake } = created;

        setData((prev) => [stake, ...prev]);
        syncBalances(balances);
      }
    } catch (error) {}
  };

  const handleComplete = async (stake) => {
    try {
      const expectedAmount = Number(((stake.amount * stake.rate) / 100).toFixed(2));
      const { result, amount } = await confirmStake(expectedAmount, stake.amount);

      setLoading(true);

      const res = await StakeService.complete_stake(token, { stake_id: stake.id, result, amount });
      const { balances, ...completedStake } = res.data;

      setData((prev) => prev.map((item) => {
        if (item.id === stake.id) {
          return {...item, ...completedStake};
        }

        return item;
      }))
      syncBalances(balances);
    } catch (error) {
      openErrorMessage(error);
    } finally {
      setLoading(false);
      closeCompleteStakeModal();
    }
  }

  if (!selectedUser) return null;

  return (
    <>
      {createStakeProps && (
        <PopModal modalToggler={createStakeProps !== null} onClose={() => {}}>
          <CreateStake
            handleOk={createStakeProps.handleOk}
            handleCancel={createStakeProps.handleCancel}
            user_id={selectedUser}
          />
        </PopModal>
      )}

      {completeStakeProps && <PopModal modalToggler={completeStakeProps !== null} onClose={() => {}}>
        <CompleteStakeModal
          handleOk={completeStakeProps.handleOk}
          handleCancel={completeStakeProps.handleCancel}
          initialAmount={completeStakeProps.initialAmount}
          stakeAmount={completeStakeProps.stakeAmount}
        />
      </PopModal>}

      <div className="main_routes bv-user-profit-log">
        <div className="headers">
          <h4>
            Staking Log For {selectedUserData.first_name}{" "}
            {selectedUserData.last_name}
          </h4>

          <button className="btn" onClick={handleAdd}>
            Add
          </button>
        </div>

        <div className="balances">
          <span>Staking Balance: {selectedUserData.staking_balance}</span>
          <span>Current Stake Balance: {selectedUserData.current_stake_balance}</span>
          <span>Stake Earnings Balance: {selectedUserData.stake_earnings_balance}</span>
        </div>

        {loading && <div className="loading">Loading...</div>}

        {!loading && selectedUser && (
          <div className="responsive-table">
            <table>
              <thead>
              <tr>
                <th>Order</th>
                <th>Date</th>
                <th>Coin</th>
                <th>Amount</th>
                <th>Interest Rate (%)</th>
                <th>Duration (Days)</th>
                <th>Status</th>
                <th>Profit</th>
                <th>Loss</th>
                <th>Complete</th>
              </tr>
              </thead>

              <tbody>
              {data.map((item) => (
                <tr key={item.id}>
                  <td>{item.id.slice(-8).toUpperCase()}</td>
                  <td>
                    {new Date(item.created_at).toISOString().split("T")[0]}
                  </td>
                  <td>{item.coin}</td>
                  <td>{item.amount}</td>
                  <td>{item.rate}</td>
                  <td>{item.duration}</td>
                  <td>{item.status}</td>
                  <td>{item.earnings}</td>
                  <td>{item.loss}</td>
                  <td>
                    {item.status === "Active" && <button
                      className={`kyc_action decline`}
                      onClick={() => handleComplete(item)}
                    >
                      Complete
                    </button>}
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="headers sub">
          <h4>Staking Withdrawals</h4>
        </div>

        {!loading && selectedUser && (
          <div className="responsive-table">
            <table>
              <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
              </thead>

              <tbody>
              {withdrawals.map((item) => (
                <tr key={item.id}>
                  <td>{item.id.slice(-8).toUpperCase()}</td>
                  <td>
                    {new Date(item.created_at).toISOString().split("T")[0]}
                  </td>
                  <td>{item.amount}</td>
                  <td>{item.status}</td>
                </tr>
              ))}

              {withdrawals.length === 0 && (
                <tr>
                  <td colSpan={4}>No staking withdrawals yet</td>
                </tr>
              )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default UserStakeLog;
